import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useCurrency } from '../contexts/CurrencyContext';
import {
  TrendingUp,
  TrendingDown,
  ShieldCheck,
  AlertTriangle,
  Wallet,
  CreditCard,
  Building2,
  PieChart as PieIcon,
  ChevronDown,
  ChevronUp,
  Sparkles,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

export default function NetWorthDashboard({ netWorthData }) {
  const { theme, isDark } = useTheme();
  const { formatCurrency } = useCurrency();
  const [showChart, setShowChart] = useState(true);

  if (!netWorthData) return null;

  const {
    netWorth,
    totalAssets,
    totalLiabilities,
    liquidCash,
    debtRatio,
    debtRating,
    debtRatingColor,
    momGrowthAmount,
    momGrowthPct,
    trajectory,
    accountCount,
  } = netWorthData;

  const isNetWorthPositive = netWorth >= 0;
  const isGrowthPositive = momGrowthAmount >= 0;

  return (
    <div className="mb-6 space-y-4">
      {/* Primary KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Net Worth Hero Card */}
        <div
          className="p-5 rounded-2xl border transition-all duration-200 relative overflow-hidden"
          style={{
            backgroundColor: theme.cardBackground,
            borderColor: theme.divider,
            boxShadow: isDark
              ? '0 4px 20px rgba(0, 0, 0, 0.4)'
              : '0 4px 20px rgba(0, 0, 0, 0.06)',
          }}
          data-testid="networth-card"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: theme.textSecondary }}>
              Total Net Worth
            </span>
            <span
              className="text-[11px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1"
              style={{
                backgroundColor: isGrowthPositive ? '#10B98118' : '#EF444418',
                color: isGrowthPositive ? '#10B981' : '#EF4444',
              }}
            >
              {isGrowthPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {isGrowthPositive ? '+' : ''}{momGrowthPct}% MoM
            </span>
          </div>
          <div
            className="text-2xl lg:text-3xl font-black tracking-tight"
            style={{ color: isNetWorthPositive ? theme.text : '#EF4444' }}
            data-testid="networth-value"
          >
            {formatCurrency(netWorth)}
          </div>
          <p className="text-xs mt-1.5 flex items-center gap-1" style={{ color: theme.textSecondary }}>
            <span>{isGrowthPositive ? '+' : ''}{formatCurrency(momGrowthAmount)} vs last month</span>
            <span>•</span>
            <span>{accountCount} accounts</span>
          </p>
        </div>

        {/* Total Assets Card */}
        <div
          className="p-5 rounded-2xl border transition-all duration-200"
          style={{
            backgroundColor: theme.cardBackground,
            borderColor: theme.divider,
            boxShadow: isDark
              ? '0 4px 20px rgba(0, 0, 0, 0.4)'
              : '0 4px 20px rgba(0, 0, 0, 0.06)',
          }}
          data-testid="assets-card"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: theme.textSecondary }}>
              Total Assets
            </span>
            <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-blue-500/10 text-blue-500">
              <Building2 className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl lg:text-3xl font-black tracking-tight text-blue-500" data-testid="assets-value">
            {formatCurrency(totalAssets)}
          </div>
          <p className="text-xs mt-1.5" style={{ color: theme.textSecondary }}>
            Bank, savings, cash & investments
          </p>
        </div>

        {/* Total Liabilities Card */}
        <div
          className="p-5 rounded-2xl border transition-all duration-200"
          style={{
            backgroundColor: theme.cardBackground,
            borderColor: theme.divider,
            boxShadow: isDark
              ? '0 4px 20px rgba(0, 0, 0, 0.4)'
              : '0 4px 20px rgba(0, 0, 0, 0.06)',
          }}
          data-testid="liabilities-card"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: theme.textSecondary }}>
              Total Liabilities
            </span>
            <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-red-500/10 text-red-500">
              <CreditCard className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl lg:text-3xl font-black tracking-tight text-red-500" data-testid="liabilities-value">
            {formatCurrency(totalLiabilities)}
          </div>
          <div className="flex items-center gap-1.5 mt-1.5 text-xs">
            <span
              className="font-bold px-1.5 py-0.5 rounded text-[10px]"
              style={{ backgroundColor: `${debtRatingColor}20`, color: debtRatingColor }}
            >
              {debtRatio}% Debt Ratio • {debtRating}
            </span>
          </div>
        </div>

        {/* Liquid Cash Card */}
        <div
          className="p-5 rounded-2xl border transition-all duration-200"
          style={{
            backgroundColor: theme.cardBackground,
            borderColor: theme.divider,
            boxShadow: isDark
              ? '0 4px 20px rgba(0, 0, 0, 0.4)'
              : '0 4px 20px rgba(0, 0, 0, 0.06)',
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: theme.textSecondary }}>
              Liquid Cash Buffer
            </span>
            <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-emerald-500/10 text-emerald-500">
              <Wallet className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl lg:text-3xl font-black tracking-tight text-emerald-500">
            {formatCurrency(liquidCash)}
          </div>
          <p className="text-xs mt-1.5" style={{ color: theme.textSecondary }}>
            Immediate checking, cash & savings
          </p>
        </div>
      </div>

      {/* 6-Month Historical Net Worth Trajectory Chart */}
      <div
        className="p-5 rounded-2xl border transition-all duration-200"
        style={{
          backgroundColor: theme.cardBackground,
          borderColor: theme.divider,
          boxShadow: isDark
            ? '0 4px 20px rgba(0, 0, 0, 0.4)'
            : '0 4px 20px rgba(0, 0, 0, 0.06)',
        }}
      >
        <div
          className="flex items-center justify-between cursor-pointer select-none"
          onClick={() => setShowChart(!showChart)}
        >
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-purple-500/10 text-purple-500">
              <TrendingUp className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-bold text-sm sm:text-base" style={{ color: theme.text }}>
                Net Worth Trajectory
              </h3>
              <p className="text-xs" style={{ color: theme.textSecondary }}>
                6-Month wealth growth and asset progression
              </p>
            </div>
          </div>

          <button
            className="p-1.5 rounded-lg transition-colors hover:bg-black/5 dark:hover:bg-white/5"
            style={{ color: theme.textSecondary }}
          >
            {showChart ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
        </div>

        {showChart && (
          <div className="mt-4 pt-3 border-t" style={{ borderColor: theme.divider }}>
            <div className="h-60 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={trajectory}
                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="netWorthGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={theme.accent} stopOpacity={0.4} />
                      <stop offset="95%" stopColor={theme.accent} stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="assetsGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#2D333B' : '#E5E7EB'} />
                  <XAxis
                    dataKey="month"
                    stroke={theme.textSecondary}
                    fontSize={11}
                    tickLine={false}
                  />
                  <YAxis
                    stroke={theme.textSecondary}
                    fontSize={11}
                    tickLine={false}
                    tickFormatter={(val) => `${val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val}`}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div
                            className="p-3 rounded-xl border text-xs shadow-xl"
                            style={{
                              backgroundColor: theme.cardBackground,
                              borderColor: theme.divider,
                              color: theme.text,
                            }}
                          >
                            <p className="font-bold mb-1.5 text-xs">{data.month}</p>
                            <div className="space-y-1">
                              <p className="flex justify-between gap-4 font-semibold text-purple-500">
                                <span>Net Worth:</span>
                                <span>{formatCurrency(data.netWorth)}</span>
                              </p>
                              <p className="flex justify-between gap-4 text-blue-500">
                                <span>Assets:</span>
                                <span>{formatCurrency(data.assets)}</span>
                              </p>
                              <p className="flex justify-between gap-4 text-red-500">
                                <span>Liabilities:</span>
                                <span>{formatCurrency(data.liabilities)}</span>
                              </p>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="assets"
                    stroke="#3B82F6"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#assetsGrad)"
                    name="Assets"
                  />
                  <Area
                    type="monotone"
                    dataKey="netWorth"
                    stroke={theme.accent}
                    strokeWidth={3}
                    fillOpacity={1}
                    fill="url(#netWorthGrad)"
                    name="Net Worth"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Asset vs Liability Comparison Bar */}
            <div className="mt-4 pt-3 border-t" style={{ borderColor: theme.divider }}>
              <div className="flex justify-between text-xs font-semibold mb-1.5" style={{ color: theme.textSecondary }}>
                <span className="text-blue-500">Assets ({formatCurrency(totalAssets)})</span>
                <span className="text-red-500">Liabilities ({formatCurrency(totalLiabilities)})</span>
              </div>
              <div className="w-full h-2.5 rounded-full overflow-hidden flex bg-gray-200 dark:bg-gray-800">
                <div
                  className="h-full bg-blue-500 transition-all duration-500"
                  style={{
                    width: `${totalAssets + totalLiabilities > 0 ? (totalAssets / (totalAssets + totalLiabilities)) * 100 : 50}%`,
                  }}
                />
                <div
                  className="h-full bg-red-500 transition-all duration-500"
                  style={{
                    width: `${totalAssets + totalLiabilities > 0 ? (totalLiabilities / (totalAssets + totalLiabilities)) * 100 : 50}%`,
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
